import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  DefaultRootPricing,
  getCompanyPricing,
  getCurrentFeesReport,
  getDefaultRootPricing,
  getFeesBillingByOrganization,
  getFeesBillingByReportId,
  getFeesReportByQuery,
  getFeesReportHistory,
  getFixedFees,
  getMinimalPricing,
  getOrganizationPricing,
  getPlanPricing,
  getPricing,
  markFeesReportAsPaid,
  MinimalPricing,
  updateCompanyPricing,
  updateOrganizationPricing,
  updatePlanPricing,
} from '@/services/Pricing/pricing.service';

export type feesReportQuery = {
  searchValue?: string;
  status: string[];
  limit: number;
  offset: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export type FeesReport = {
  _id: string;
  feesReportId?: string;
  residualsEarned?: number;
  transactionCount?: number;
  totalFeesCollected?: number;
  totalFinalFee?: number;
  totalPaymentVolume?: number;
  status?: string;
  datePaid?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
  name?: string;
  logo?: string;
};

export type FeesReportHistoryResponse = {
  data: FeesReport[];
  totalCount: number;
};

export type PlatformBilling = {
  _id: string;
  id?: string;
  name: string;
  transactionCount?: number;
  totalPaymentAmount?: number;
  totalPaymentVolume?: number;
  finalPlatformFee?: number;
  totalResidualApplicationFee?: number;
};

export type ResidualsReportByQueryResponse = {
  data: FeesReport[];
  totalCount: number;
};

export const pricingApiSlice = createApi({
  reducerPath: 'pricingApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'DefaultPricing',
    'MinimalPricing',
    'OrganizationPricing',
    'PlanPricing',
    'CompanyPricing',
    'FeesReport',
  ],
  endpoints: (builder) => ({
    getPricing: builder.query<any, { organizationId: string; companyId?: string }>({
      async queryFn(params) {
        try {
          const data = await getPricing(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getDefaultRootPricing: builder.query<
      DefaultRootPricing,
      { organizationId: string; companyId?: string }
    >({
      async queryFn(params) {
        const data = await getDefaultRootPricing(params);
        return { data };
      },
      providesTags: ['DefaultPricing'],
    }),
    getMinimalPricing: builder.query<
      MinimalPricing,
      { organizationId: string; companyId?: string }
    >({
      async queryFn(params) {
        const data = await getMinimalPricing(params);
        return { data };
      },
      providesTags: ['MinimalPricing'],
    }),
    updateCompanyPricing: builder.mutation<
      any,
      { organizationId: string; companyId: string; pricing: MinimalPricing }
    >({
      async queryFn(params) {
        try {
          const data = await updateCompanyPricing(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['MinimalPricing'],
    }),
    getPlanPricing: builder.query<MinimalPricing, { organizationId: string; planId: string }>({
      async queryFn(params) {
        const data = await getPlanPricing(params);
        return { data };
      },
      providesTags: ['PlanPricing'],
    }),
    getOrganizationPricing: builder.query<MinimalPricing, string>({
      async queryFn(organizationId) {
        const data = await getOrganizationPricing(organizationId);
        return { data };
      },
      providesTags: ['OrganizationPricing'],
    }),
    updateOrganizationPricing: builder.mutation<
      any,
      { organizationId: string; pricing: MinimalPricing }
    >({
      async queryFn(params) {
        try {
          const data = await updateOrganizationPricing(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['OrganizationPricing'],
    }),
    updatePlanPricing: builder.mutation<
      any,
      { organizationId: string; planId: string; pricing: MinimalPricing }
    >({
      async queryFn(params) {
        try {
          const data = await updatePlanPricing(params);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: ['PlanPricing'],
    }),
    getFixedFees: builder.query<any, string>({
      async queryFn(companyId) {
        const data = await getFixedFees(companyId);
        return { data };
      },
      providesTags: ['OrganizationPricing'],
    }),
    getCompanyPricing: builder.query<any, { companyId: string }>({
      async queryFn(params) {
        const data = await getCompanyPricing(params.companyId);
        return { data };
      },
      providesTags: ['CompanyPricing'],
    }),
    getCurrentPlatformReport: builder.query<FeesReport, { companyId: string }>({
      async queryFn(params) {
        const data = await getCurrentFeesReport(params.companyId);
        return { data };
      },
    }),
    getFeesReportHistory: builder.query<
      FeesReportHistoryResponse,
      {
        companyId: string;
        query?: {
          limit?: number;
          offset?: number;
          sortColumn?: string;
          sortValue?: 'asc' | 'desc';
        };
      }
    >({
      async queryFn(params) {
        const data = await getFeesReportHistory(params.companyId, params.query);
        return { data };
      },
    }),
    getPlatformBillingByOrg: builder.query<PlatformBilling[], { organizationId: string }>({
      async queryFn(params) {
        const data = await getFeesBillingByOrganization(params.organizationId);
        return { data };
      },
    }),
    getPlatformBillingByReport: builder.query<
      PlatformBilling[],
      {
        feesReportId: string;
        query?: { searchValue?: string; limit?: number; offset?: number };
      }
    >({
      async queryFn(params) {
        const data = await getFeesBillingByReportId(params.feesReportId, params.query);
        return { data };
      },
    }),
    getResidualsReportByQuery: builder.query<
      ResidualsReportByQueryResponse,
      { companyId: string; query: feesReportQuery }
    >({
      async queryFn(params) {
        const data = await getFeesReportByQuery(params.companyId, params.query);
        return { data };
      },
      providesTags: ['FeesReport'],
    }),
    markFeesReportAsPaid: builder.mutation<any, { feesReportId: string; companyId: string }>({
      async queryFn(params) {
        try {
          const data = await markFeesReportAsPaid(params.companyId, params.feesReportId, 'paid');
          return { data };
        } catch (error: any) {
          return {
            error,
          };
        }
      },
      invalidatesTags: ['FeesReport'],
    }),
  }),
});

export const {
  useGetPricingQuery,
  useLazyGetPricingQuery,
  useGetDefaultRootPricingQuery,
  useGetMinimalPricingQuery,
  useLazyGetMinimalPricingQuery,
  useUpdateCompanyPricingMutation,
  useGetOrganizationPricingQuery,
  useUpdateOrganizationPricingMutation,
  useGetPlanPricingQuery,
  useUpdatePlanPricingMutation,
  useGetFixedFeesQuery,
  useGetCompanyPricingQuery,
  useGetCurrentPlatformReportQuery,
  useGetFeesReportHistoryQuery,
  useGetPlatformBillingByOrgQuery,
  useGetPlatformBillingByReportQuery,
  useLazyGetFeesReportHistoryQuery,
  useGetResidualsReportByQueryQuery,
  useMarkFeesReportAsPaidMutation,
} = pricingApiSlice;
